import * as React from "react";

import { Popup, Label } from "semantic-ui-react";
import * as moment from "moment";
import { debounce } from "lodash";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { Appointment as AppointmentModel, Direction, APPOINTMENT_DATA_FORMAT,
    AppointmentAndParent, AppointmentStatus, ChildAppointment as ChildAppointmentModel, Clinic } from "./models";
import { CalendarMouseEventDebounceMilliseconds } from "./Calendar";
import { AppointmentDetails } from "./AppointmentDetails";

interface Props {
    appointment: AppointmentModel | Partial<ChildAppointmentModel>;
    mergedAppointments?: AppointmentModel[];
    parentAppointment?: Clinic;
    hourSize: number;
    timeDirection: Direction;
    issues?: string[];
    level?: number;
    parentStart?: moment.Moment;
    parentEnd?: moment.Moment;
    isEmpty?: boolean;
    onDrop?: (itemId: string, appointment: AppointmentAndParent) => void;
    onClick?: (appointment: AppointmentAndParent) => void;
}

enum CalendarLevel {
    TopLevelAppointment = 0,
    ChildAppointment = 2
}

export const Appointment: React.FunctionComponent<Props> = (props: Props) => {
    const openPopupDebounced = debounce(() => {
        if (props.level === CalendarLevel.ChildAppointment || props.appointment.appointments.length === 0) {
            setPopupOpen(true);
        }
    }, CalendarMouseEventDebounceMilliseconds);

    const [popupOpen, setPopupOpen] = React.useState<boolean>(false);

    const getAppointmentClassName = (
        level: number,
        isEmpty: boolean,
        isTimeNote: boolean,
        status: AppointmentStatus,
        mergedAppointments?: AppointmentModel[]
    ) => {
        return [
            "appointment",
            `level-${level}`,
            isEmpty ? "empty-slot" : "",
            popupOpen ? "drag-over" : "",
            isTimeNote ? "time-note" : "",
            getAppointmentStatusCssClass(level, status, mergedAppointments),
        ].join(" ");
    };

    const getAppointmentStatusCssClass = (level: number, status: AppointmentStatus, mergedAppointments?: AppointmentModel[]) => {
        if (props.mergedAppointments?.length > 0)
        {
            if (mergedAppointments.some(a => a.status === AppointmentStatus.EmployeeCheckedIn)) {
                return "employee-checked-in";
            }

            if (mergedAppointments.some(a => a.status === AppointmentStatus.DidNotAttend)) {
                return "dna";
            }

            if (mergedAppointments.some(a => a.status !== AppointmentStatus.Completed))
            {
                return "merged-appointment";
            }

            return "completed";
        }

        switch (status) {
            case AppointmentStatus.None:
                return "";
            case AppointmentStatus.Pending:
                return "pending";
            case AppointmentStatus.DidNotAttend:
                return "dna";
            case AppointmentStatus.EmployeeCheckedIn:
                return "employee-checked-in";
            case AppointmentStatus.Completed:
                return "completed";
            default:
                return "";
        }
    };

    const onDragOver = (event: React.DragEvent<HTMLElement>) => {
        if (!props.onDrop || event.dataTransfer.types.indexOf(APPOINTMENT_DATA_FORMAT) === -1) {
            return;
        }
        if (props.appointment.ohCaseId) {
            event.stopPropagation();
            return; // Do not allow dropping on already booked appointment
        }
        event.preventDefault();
    };

    const onDrop = (event: React.DragEvent<HTMLElement>) => {
        closePopup();
        if (props.onDrop && event.dataTransfer.types.indexOf(APPOINTMENT_DATA_FORMAT) > -1) {
            event.stopPropagation();
            const id = event.dataTransfer.getData(APPOINTMENT_DATA_FORMAT);
            // We should revisit this code when we have a better understanding of the data model to avoid this cast
            props.onDrop(id, { ...props.appointment, parentAppointment: props.parentAppointment } as AppointmentAndParent);
        }
    };

    const onClick = (e: React.MouseEvent) => {
        if (props.onClick) {
            // We should revisit this code when we have a better understanding of the data model to avoid this cast
            props.onClick({ ...props.appointment, parentAppointment: props.parentAppointment } as AppointmentAndParent);
            e.stopPropagation();
        }
    };

    const closePopup = () => {
        openPopupDebounced.cancel();
        setPopupOpen(false);
    };

    const appointmentPopupTaskTypes = () => {
        if (props.mergedAppointments) {
            return props.mergedAppointments.map(mergedAppointment =>
                <Popup.Header key={mergedAppointment.taskTypes[0].type}>{mergedAppointment.taskTypes[0].description}</Popup.Header>);
        }

        if (props.appointment.taskTypes)
        {
            return props.appointment.taskTypes.map(taskType =>
                <Popup.Header key={taskType.type}>{taskType.description}</Popup.Header>);
        }

        return React.Fragment;
    };

    const diariesRevampPart1 = getFeatureToggles()[FeatureToggleNames.DiariesRevampPart1];

    const { hourSize, timeDirection, isEmpty } = props;
    const { id, status } = props.appointment;
    const start = diariesRevampPart1 ? moment(props.appointment.start) : props.appointment.start;
    const end = diariesRevampPart1 ? moment(props.appointment.end) : props.appointment.end;

    const parentStart = props.parentStart || start;
    const marginStart = Math.max(start.diff(parentStart, "hours", true) * hourSize - 2, 0);
    const paddingStart = (marginStart === 0 ? 3 : 1);
    const paddingEnd = (end.isSame(props.parentEnd) ? 2 : 0);
    const size = end.diff(start, "hours", true) * hourSize - paddingStart - paddingEnd;
    const divStyle = timeDirection === "vertical"
        ? { height: size, top: marginStart }
        : { width: size, left: marginStart };
    const level = props.level || 0;
    const issues = props.issues || [];
    const isTimeNote = !props.appointment.ohCaseId && !props.appointment.clinicType;

    const mainAppointment = props.parentAppointment ? props.parentAppointment : props.appointment;
    const clinicLocationTypes = mainAppointment.clinicLocationTypes && !!mainAppointment.clinicLocationTypes.length
        && <Popup.Content>{mainAppointment.clinicLocationTypes.join(", ")}</Popup.Content>;
    const roomsNames = mainAppointment.rooms && !!mainAppointment.teamPreventUsers.length
        && <Popup.Content>{mainAppointment.rooms.map(r => r.name).join(", ")}</Popup.Content>;
    const roomsAddressLines1 = mainAppointment.rooms && !!mainAppointment.teamPreventUsers.length
        && <Popup.Content>{mainAppointment.rooms.map(r => r.addressLine1).join(", ")}</Popup.Content>;

    return (
        <Popup
            className="appointment-popover"
            position={level ? "right center" : "left center"}
            popperModifiers={[{ name: "preventOverflow", options: { boundariesElement: "window" } }]}
            trigger={
                <div
                    /* eslint-disable-next-line react/forbid-dom-props */
                    style={divStyle}
                    className={getAppointmentClassName(level, isEmpty, isTimeNote, status, props.mergedAppointments)}
                    onClick={onClick}
                    onMouseOver={openPopupDebounced}
                    onMouseLeave={closePopup}
                    onDragEnter={openPopupDebounced}
                    onDragOver={onDragOver}
                    onDragLeave={closePopup}
                    onDrop={onDrop}
                >
                    {isTimeNote && <div className="description">{props.appointment.bookingTypeId}</div>}
                    {isTimeNote && <div className="description">{props.appointment.description}</div>}
                    {props?.appointment?.employee && <div className="description">{props.appointment.employee?.name}</div>}
                    {!props.mergedAppointments && props.appointment.taskTypes && !!props.appointment.taskTypes.length &&
                        <div className="description">{props.appointment.taskTypes.map(t => t.description).join(", ")}</div>}
                    {props.mergedAppointments && <div className="description">Appointments: {props.mergedAppointments.length}</div>}
                    <AppointmentDetails
                        appointment={props.appointment}
                        hourSize={hourSize}
                        timeDirection={timeDirection}
                        level={level + 1}
                        onClick={props.onClick}
                        onDrop={props.onDrop}
                        parentId={id}
                        parentStart={start}
                        parentEnd={end}
                    />
                    <div className="issues">
                        {issues.map(issue => <Label size="tiny" color="red" content={issue} key={issue} />)}
                    </div>
                </div>
            }
            open={popupOpen}
        >
            <>
                {appointmentPopupTaskTypes()}
                {props.appointment.employee &&
                    <Popup.Content>{props.appointment.employee.name}</Popup.Content>}
                {props.appointment.bookingTypeId &&
                    <Popup.Content>{props.appointment.bookingTypeId}</Popup.Content>}
                {props.appointment.description &&
                    <Popup.Content>{props.appointment.description}</Popup.Content>}
                <Popup.Content>{start.format("HH:mm")}-{end.format("HH:mm")}</Popup.Content>
                {props.appointment.teamPreventUsers && !!props.appointment.teamPreventUsers.length &&
                    <Popup.Content>{props.appointment.teamPreventUsers.map(u => u.name).join(", ")}</Popup.Content>}
                {clinicLocationTypes}
                {props.appointment.clinicType &&
                    <Popup.Content>{props.appointment.clinicType}</Popup.Content>}
                {props.appointment.customer &&
                    <Popup.Content>{props.appointment.customer.name}</Popup.Content>}
                {roomsNames}
                {roomsAddressLines1}
            </>
        </Popup>
    );
};
